import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Eye, Tag, Package, User, Loader2, Share2, Copy, Check } from 'lucide-react';
import AppFooter from '../components/AppFooter';
import { BACKEND_URL } from '../config/api';

export default function PublicDesignPage() {
  const { designId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Store referral source
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      sessionStorage.setItem('kura_referral', ref);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchDesign = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/designs/${designId}/public`);
        if (res.ok) {
          setDesign(await res.json());
        }
      } catch {
        // Not found
      } finally {
        setLoading(false);
      }
    };
    fetchDesign();
  }, [designId]);

  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: design?.title || 'Craft Design',
        text: `Check out this design on Kura Stash: ${design?.title}`,
        url: window.location.href,
      });
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="animate-spin text-sage" size={32} />
      </div>
    );
  }

  if (!design) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center text-center p-4">
        <h1 className="text-2xl font-light text-charcoal mb-2">Design Not Found</h1>
        <p className="text-sm text-gray-soft mb-6">This design may have been removed or made private.</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 bg-sage text-white rounded-lg text-sm hover:bg-sage-dark transition-colors"
        >
          Explore Kura Stash
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="public-design-page">
      {/* Header */}
      <nav className="bg-white border-b border-beige sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Sparkles className="h-6 w-6 text-sage" />
            <span className="text-xl font-light text-charcoal">Kura Stash</span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-sage text-white rounded-lg text-sm hover:bg-sage-dark transition-colors"
            data-testid="get-started-btn"
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Design Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white rounded-xl border border-beige overflow-hidden shadow-sm">
          {/* Image */}
          {design.image_url && (
            <div className="aspect-video bg-cream">
              <img
                src={design.image_url}
                alt={design.title}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Title & Share */}
            <div className="flex items-start justify-between gap-4 mb-4">
              <h1 className="text-2xl font-medium text-charcoal" data-testid="public-design-title">
                {design.title}
              </h1>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleCopyLink}
                  className="p-2 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-colors"
                  title="Copy link"
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check size={18} className="text-sage" /> : <Copy size={18} />}
                </button>
                <button
                  onClick={handleShare}
                  className="p-2 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-colors"
                  title="Share"
                  data-testid="share-btn"
                >
                  <Share2 size={18} />
                </button>
              </div>
            </div>

            {/* View count */}
            {design.view_count > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-soft mb-4">
                <Eye size={12} />
                {design.view_count} view{design.view_count !== 1 ? 's' : ''}
              </div>
            )}

            {/* Description */}
            {design.description && (
              <p className="text-sm text-gray-soft whitespace-pre-wrap mb-6">{design.description}</p>
            )}

            {/* Creator Badge */}
            {design.creator && (
              <div className="flex items-center gap-3 p-4 bg-cream rounded-lg mb-6" data-testid="creator-badge">
                {design.creator.picture ? (
                  <img src={design.creator.picture} alt={design.creator.name} className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-sage-light flex items-center justify-center">
                    <User size={18} className="text-sage" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-charcoal text-sm">{design.creator.name}</p>
                  {design.creator.handle && (
                    <p className="text-xs text-sage">@{design.creator.handle}</p>
                  )}
                  {design.creator.bio && (
                    <p className="text-xs text-gray-soft mt-0.5">{design.creator.bio}</p>
                  )}
                </div>
              </div>
            )}

            {/* Technique Tags */}
            {design.technique_tags?.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-1 text-xs text-gray-soft mb-2">
                  <Tag size={12} />
                  Techniques
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {design.technique_tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-sage/10 text-sage text-xs rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Products Used */}
            {design.products_used?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-1 text-xs text-gray-soft mb-2">
                  <Package size={12} />
                  Products Used ({design.products_used.length})
                </div>
                <div className="space-y-1.5">
                  {design.products_used.map((p, i) => (
                    <div key={p.item_id || i} className="flex items-center gap-2 px-3 py-2 bg-cream rounded-lg">
                      <span className="text-sm text-charcoal">{p.name}</span>
                      {p.brand && <span className="text-xs text-gray-soft">by {p.brand}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="border-t border-beige pt-6 mt-6 text-center">
              <p className="text-sm text-gray-soft mb-4">
                Organize your craft supplies and share your creations
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors font-medium"
                data-testid="cta-get-started"
              >
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </div>

      <AppFooter />
    </div>
  );
}
